import React, { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { setUnauthorizedHandler } from "@/lib/api";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import ArchivePage from "@/pages/ArchivePage";
import DisclaimerModal from "@/components/DisclaimerModal";
import { TestBanner } from "@/components/TestBanner";

function Protected({ children }) {
  const { auth } = useAuth();
  if (!auth) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  const { auth, logout } = useAuth();
  const navigate = useNavigate();
  const redirecting = useRef(false);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      if (redirecting.current) return;
      redirecting.current = true;
      logout();
      if (window.location.pathname !== "/login") {
        toast.error("Sessione scaduta, effettua di nuovo l'accesso");
        navigate("/login", { replace: true });
      }
      setTimeout(() => { redirecting.current = false; }, 1500);
    });
    return () => setUnauthorizedHandler(null);
  }, [navigate, logout]);

  return (
    <Routes>
      <Route path="/login" element={auth ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/" element={<Protected><DashboardPage /></Protected>} />
      <Route path="/archivio" element={<Protected><ArchivePage /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  // Global safety guard: some Radix dialogs lock document.body {pointer-events:none}
  // and, if they unmount abruptly (e.g. a new alarm modal appears while a dialog is
  // open), the lock can stay and freeze the whole page. This ensures the body is
  // always interactive whenever no Radix dialog is actually present in the DOM.
  useEffect(() => {
    const id = setInterval(() => {
      const hasDialog = document.querySelector('[role="dialog"],[role="alertdialog"]');
      if (!hasDialog && document.body.style.pointerEvents === "none") {
        document.body.style.pointerEvents = "";
      }
    }, 600);
    return () => clearInterval(id);
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <TestBanner />
        <DisclaimerModal />
        <AppRoutes />
        <Toaster position="bottom-right" richColors theme="dark" />
      </BrowserRouter>
    </AuthProvider>
  );
}
